import { useRouter } from "next/router";
import ErrorPage from "next/error";
import Container from "../../../components/Container";
import PostBody from "../../../components/post-body";
import MoreStories from "../../../components/more-stories";
import Header from "../../../components/Header";
import Cta from "../../../components/Cta";
import Button from "../../../components/Button";
import Footer from "../../../components/Footer";
import NewsSlider from "../../../components/NewsSlider";
import ResourceHero from "../../../components/ResourceHero";
import ResourceAuthors from "../../../components/ResourceAuthors";
import ResourceShare from "../../../components/ResourceShare";
import PostHeader from "../../../components/post-header";
import SectionSeparator from "../../../components/section-separator";
import Layout from "../../../components/layout";
import {
  getResourceBySlug,
  getResources,
  getCustomOptions,
  getAllPostsForHome,
} from "../../../lib/api";
import PostTitle from "../../../components/post-title";
import Head from "next/head";
import { CMS_NAME } from "../../../lib/constants";
import Tags from "../../../components/tags";
import styles from "./styles.module.css";

export default function Resource({
  resource,
  preview,
  resources,
  footerData,
  ctaData,
  shareData,
  slug,
}) {
  const router = useRouter();
  if (!resource) {
    return <ErrorPage statusCode={404} />;
  }

  const type = resource?.categories?.nodes?.length
    ? resource?.categories?.nodes[0].slug
    : null;

  return (
    <Layout preview={preview} seo={resource?.seo}>
      <div className={styles.resource}>
        <Header isDark={true} />
        <Container className={styles.container}>
          <ResourceHero
            resourceType={type}
            backgroundUrl={resource?.featuredImage?.node?.sourceUrl}
            title={resource.title}
            date={resource.date}
            slug={resource.slug}
            avatarUrl={resource.author.node.avatar.url}
            author={resource.author.node.name}
            categories={resource.categories}
            isExternal={
              type === "press-release" ||
              type === "podcast" ||
              type === "news-feature"
            }
            isDownloadable={type === "report"}
            {...(type === "podcast" ? resource.podcastFields : {})}
            {...(type === "report" ? resource.reportFields : {})}
            {...(type === "speaking-engagement" ? resource.speakingFields : {})}
            {...(type === "news-feature" ? resource.pressFields : {})}
            {...resource.commonFields}
            label={resource.commonFields.label}
            link={
              resource.commonFields.externalLink ||
              resource.reportFields?.report?.node?.sourceUrl
            }
          />
          <div className={styles.content}>
            <ResourceShare
              isFacebook={shareData?.shareFacebook}
              isTwitter={shareData?.shareTwitter}
              isLinkedin={shareData?.shareLinkedin}
              isEmail={shareData?.shareEmail}
            />
            <div
              className={styles.html}
              dangerouslySetInnerHTML={{ __html: resource.content }}
            ></div>
            {type === "report" && resource.reportFields.report && (
              <div className={styles.buttonWrapper}>
                <Button href={resource.reportFields.report.node?.sourceUrl} type="green">
                  Download the report
                </Button>
              </div>
            )}
            {type === "speaking-engagement" &&
              resource.commonFields.externalLink && (
                <div className={styles.buttonWrapper}>
                  <Button
                    href={resource.commonFields.externalLink}
                    type="green"
                  >
                    Attend the event
                  </Button>
                </div>
              )}
            {type === "podcast" && resource.commonFields.externalLink && (
              <div className={styles.buttonWrapper}>
                <Button href={resource.commonFields.externalLink} type="green">
                  Listen to the episode
                </Button>
              </div>
            )}
            {type === "press-release" && resource.commonFields.externalLink && (
              <div className={styles.buttonWrapper}>
                <Button href={resource.commonFields.externalLink} type="green">
                  Read the article
                </Button>
              </div>
            )}
            {type === "news-feature" && resource.commonFields.externalLink && (
              <div className={styles.buttonWrapper}>
                <Button href={resource.commonFields.externalLink} type="green">
                  Read the article
                </Button>
              </div>
            )}
          </div>
          <ResourceAuthors
            resourceType={type}
            author={resource.author.node}
            {...resource.commonFields}
            {...(type === "podcast" ? resource.podcastFields : {})}
            {...(type === "report" ? resource.reportFields : {})}
            {...(type === "press-release" ? resource.pressFields : {})}
            {...(type === "speaking-engagement" ? resource.speakingFields : {})}
            {...(type === "news-feature" ? resource.pressFields : {})}
          />
        </Container>
        <NewsSlider posts={resources} />
        {ctaData && (
          <Cta
            title={ctaData.title}
            subtitle={ctaData.subtitle}
            icon={ctaData.icon}
            button={ctaData.button}
          />
        )}
      </div>
      {footerData && <Footer data={footerData} />}
    </Layout>
  );
}

export async function getStaticProps({ params, preview = false, previewData }) {
  const resource = await getResourceBySlug(params.slug, preview, previewData);
  const { nodes: resources } = await getResources(null, preview, previewData);
  const { edges: posts } = await getAllPostsForHome(preview);
  const { footer, cta, share } = await getCustomOptions(preview);
  return {
    props: {
      resources: resources,
      preview,
      resource,
      footerData: footer,
      shareData: share,
      ctaData: cta,
      slug: params.slug,
    },
    revalidate: 10,
  };
}

export async function getStaticPaths() {
  const resources = await getResources(null);
  return {
    paths: resources.nodes.map((node) => `/resources/${node.slug}`) || [],
    fallback: true,
  };
}
